import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { setLoading } from "./LoadingSlice";
import BaseUrl from '../../BaseUrl';

const API_BASE_URL = `${BaseUrl.invoiceurl}PaymentTypeMasters`;

// Async Thunk for fetching payment methods
export const fetchPaymentMethods = createAsyncThunk(
  'paymentMethods/fetchPaymentMethods',
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(API_BASE_URL);
      // Directly access response.data as it's an array now
      const filteredMethods = response.data.filter(method =>
        ['Cash', 'Insurance', 'Cashfree'].includes(method.PaymentType)
      ).map(method => ({
        id: method.Id,
        paymentType: method.PaymentType,
        status: method.Status,
        initialStatus: method.Status // Store initial status to check for changes
      }));
      return filteredMethods;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async Thunk for updating a payment method
export const updatePaymentMethodStatus = createAsyncThunk(
  'paymentMethods/updatePaymentMethodStatus',
  async ({ paymentType, status }, { rejectWithValue }) => {
    try {
      // Find the ID of the method to update from the current state if needed
      // For a POST, the backend typically handles mapping based on paymentType
      const response = await axios.post(API_BASE_URL, { PaymentType: paymentType, Status: status });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const paymentMethodsSlice = createSlice({
  name: 'paymentMethods',
  initialState: {
    methods: [],
    loading: false,
    error: null,
    changedMethods: {}
  },
  reducers: {
    togglePaymentMethod: (state, action) => {
      const { paymentType } = action.payload;
      const methodIndex = state.methods.findIndex(method => method.paymentType === paymentType);
      if (methodIndex !== -1) {
        state.methods[methodIndex].status = !state.methods[methodIndex].status;
        state.changedMethods[paymentType] = state.methods[methodIndex].status !== state.methods[methodIndex].initialStatus;
      }
    },
    resetChangedMethods: (state) => {
        state.changedMethods = {};
    },
    markMethodsAsSaved: (state) => {
        state.methods.forEach(method => {
            method.initialStatus = method.status;
        });
        state.changedMethods = {};
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPaymentMethods.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPaymentMethods.fulfilled, (state, action) => {
        state.loading = false;
        state.methods = action.payload;
      })
      .addCase(fetchPaymentMethods.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(updatePaymentMethodStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePaymentMethodStatus.fulfilled, (state, action) => {
        state.loading = false;
        // The individual update successful, state update will be handled by markMethodsAsSaved after all updates
      })
      .addCase(updatePaymentMethodStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { togglePaymentMethod, resetChangedMethods, markMethodsAsSaved } = paymentMethodsSlice.actions;
export default paymentMethodsSlice.reducer;